import clsx from "clsx";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import styles from "./Tooltip.module.css";

import type React from "react";
import type { TooltipCoords, TooltipPosition, TooltipProps } from "./types";

const VIEWPORT_MARGIN = 8;
const ANCHOR_GAP = 8;

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  position = "right",
  className = "",
  usePortal = false,
  ...rest
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [effectivePosition, setEffectivePosition] =
    useState<TooltipPosition>(position);
  const [coords, setCoords] = useState<TooltipCoords | null>(null);
  // Horizontal nudge (px) applied to a non-portal top/bottom popup. CSS centres
  // it on the anchor (left: 50% / translateX(-50%)), which can still overflow
  // the viewport near screen edges, so we shift it back in.
  const [shiftX, setShiftX] = useState(0);
  // Vertical counterpart for a non-portal left/right popup. CSS centres it on
  // the anchor (top: 50% / translateY(-50%)), so an anchor near the top/bottom
  // edge can overflow vertically; we shift it back in.
  const [shiftY, setShiftY] = useState(0);
  // Portal container: the app root (`section.cashier`). Inside the MFE shadow
  // root the CSS-module styles and theme variables live under it, so portaling
  // there (rather than document.body) keeps the popup styled while still
  // escaping any clipping ancestor like a react-select menu.
  const [portalTarget, setPortalTarget] = useState<Element | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Measure the tooltip against the viewport. A left/right placement that
  // overflows sideways flips above/below; a top/bottom placement gets nudged
  // horizontally so it stays within the viewport margin. Memoized so the layout
  // effect keeps a stable dependency.
  const adjustPosition = useCallback(() => {
    const tooltipEl = tooltipRef.current;
    const anchorEl = anchorRef.current;
    if (!tooltipEl || !anchorEl) return;
    const rect = tooltipEl.getBoundingClientRect();
    const anchorRect = anchorEl.getBoundingClientRect();

    // A left/right placement that overflows sideways flips above/below. Once
    // flipped, this effect re-runs (effectivePosition is a dep) to clamp below.
    if (effectivePosition === "left" || effectivePosition === "right") {
      const overflowsHorizontally =
        rect.right > window.innerWidth - VIEWPORT_MARGIN ||
        rect.left < VIEWPORT_MARGIN;
      if (overflowsHorizontally) {
        const fitsAbove =
          anchorRect.top - rect.height - VIEWPORT_MARGIN >= VIEWPORT_MARGIN;
        setEffectivePosition(fitsAbove ? "top" : "bottom");
        return;
      }

      // Stays left/right: CSS centres the popup vertically on the anchor, so
      // compute its natural edges from the anchor centre (independent of any
      // shift already applied) and nudge it to keep it within the margin.
      const centerY = anchorRect.top + anchorRect.height / 2;
      const naturalTop = centerY - rect.height / 2;
      const naturalBottom = centerY + rect.height / 2;
      let shift = 0;
      if (naturalBottom > window.innerHeight - VIEWPORT_MARGIN) {
        shift = window.innerHeight - VIEWPORT_MARGIN - naturalBottom;
      } else if (naturalTop < VIEWPORT_MARGIN) {
        shift = VIEWPORT_MARGIN - naturalTop;
      }
      setShiftY(shift);
      return;
    }

    // top/bottom: CSS centres the popup on the anchor, so compute its natural
    // edges from the anchor centre (independent of any shift already applied)
    // and nudge it horizontally to keep it within the viewport margin.
    const centerX = anchorRect.left + anchorRect.width / 2;
    const naturalLeft = centerX - rect.width / 2;
    const naturalRight = centerX + rect.width / 2;
    let shift = 0;
    if (naturalRight > window.innerWidth - VIEWPORT_MARGIN) {
      shift = window.innerWidth - VIEWPORT_MARGIN - naturalRight;
    } else if (naturalLeft < VIEWPORT_MARGIN) {
      shift = VIEWPORT_MARGIN - naturalLeft;
    }
    setShiftX(shift);
  }, [effectivePosition]);

  useLayoutEffect(() => {
    if (!isVisible) {
      setEffectivePosition(position);
      setCoords(null);
      setShiftX(0);
      setShiftY(0);
      return;
    }
    // Portal placement is handled by the dedicated effect below.
    if (usePortal) return;
    adjustPosition();
  }, [isVisible, position, usePortal, adjustPosition]);

  // Resolve the portal container from the anchor's own subtree (so we stay
  // inside the same shadow root / styled scope), falling back to the body.
  useLayoutEffect(() => {
    if (!isVisible || !usePortal) {
      setPortalTarget(null);
      return;
    }
    setPortalTarget(anchorRef.current?.closest(".cashier") ?? document.body);
  }, [isVisible, usePortal]);

  // --- Portal: compute fixed coordinates from the anchor so the popup can live
  // outside any clipping ancestor (e.g. a react-select menu).
  // Recomputed on scroll/resize since the anchor may sit inside a scroll area.
  useLayoutEffect(() => {
    if (!isVisible || !usePortal || !portalTarget) return;

    const place = () => {
      const anchorEl = anchorRef.current;
      const tooltipEl = tooltipRef.current;
      if (!anchorEl || !tooltipEl) return;
      const anchorRect = anchorEl.getBoundingClientRect();
      const { width: w, height: h } = tooltipEl.getBoundingClientRect();

      // Flip a horizontal placement above/below when it would overflow the
      // viewport sideways — mirrors the non-portal behaviour.
      let pos: TooltipPosition = position;
      if (
        (pos === "right" &&
          anchorRect.right + ANCHOR_GAP + w >
            window.innerWidth - VIEWPORT_MARGIN) ||
        (pos === "left" && anchorRect.left - ANCHOR_GAP - w < VIEWPORT_MARGIN)
      ) {
        const fitsAbove =
          anchorRect.top - h - VIEWPORT_MARGIN >= VIEWPORT_MARGIN;
        pos = fitsAbove ? "top" : "bottom";
      }

      let left = 0;
      let top = 0;
      switch (pos) {
        case "right":
          left = anchorRect.right + ANCHOR_GAP;
          top = anchorRect.top + anchorRect.height / 2 - h / 2;
          break;
        case "left":
          left = anchorRect.left - ANCHOR_GAP - w;
          top = anchorRect.top + anchorRect.height / 2 - h / 2;
          break;
        case "top":
          left = anchorRect.left + anchorRect.width / 2 - w / 2;
          top = anchorRect.top - ANCHOR_GAP - h;
          break;
        case "bottom":
          left = anchorRect.left + anchorRect.width / 2 - w / 2;
          top = anchorRect.bottom + ANCHOR_GAP;
          break;
      }

      const maxLeft = window.innerWidth - VIEWPORT_MARGIN - w;
      const maxTop = window.innerHeight - VIEWPORT_MARGIN - h;
      left = Math.min(
        Math.max(left, VIEWPORT_MARGIN),
        Math.max(VIEWPORT_MARGIN, maxLeft)
      );
      top = Math.min(
        Math.max(top, VIEWPORT_MARGIN),
        Math.max(VIEWPORT_MARGIN, maxTop)
      );

      setEffectivePosition(pos);
      setCoords({ left, top });
    };

    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [isVisible, usePortal, position, portalTarget]);

  // Non-portal nudge: top/bottom is centred on X (shiftX), left/right on Y
  // (shiftY). The CSS variant already supplies the base translate, so we only
  // override the transform when a nudge is actually needed.
  let nonPortalStyle: React.CSSProperties | undefined;
  if (
    shiftX !== 0 &&
    (effectivePosition === "top" || effectivePosition === "bottom")
  ) {
    nonPortalStyle = { transform: `translateX(calc(-50% + ${shiftX}px))` };
  } else if (
    shiftY !== 0 &&
    (effectivePosition === "left" || effectivePosition === "right")
  ) {
    nonPortalStyle = { transform: `translateY(calc(-50% + ${shiftY}px))` };
  }

  const popup = (
    <div
      ref={tooltipRef}
      className={clsx(styles.tooltip, styles[effectivePosition])}
      style={
        usePortal
          ? {
              position: "fixed",
              left: coords?.left ?? 0,
              top: coords?.top ?? 0,
              right: "auto",
              bottom: "auto",
              transform: "none",
              visibility: coords ? "visible" : "hidden",
            }
          : nonPortalStyle
      }
    >
      <div className={styles.tooltipContent}>{content}</div>
      <div className={styles.arrow} />
    </div>
  );

  return (
    <div className={clsx(styles.tooltipWrapper, className)} {...rest}>
      <div
        ref={anchorRef}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        {children}
      </div>
      {isVisible &&
        (usePortal ? portalTarget && createPortal(popup, portalTarget) : popup)}
    </div>
  );
};
